import React, { useEffect, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Card } from 'components/Card';
import { H2, Text } from 'components/Typography';
import { theme, toRem } from 'design';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { IStore } from 'store/types';
import { getUserLatestPayoutsRequest, User } from 'store/User';
import { centsToDollarsDisplay } from 'utils/currency';
import { formatDateAndTime } from 'utils/date';

const Layout = styled(Card)`
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: flex-start;
  padding: ${toRem(theme.space.lg)};
  gap: ${toRem(theme.space.sm)};

  p {
    color: ${theme.color.neutral[500]};
  }
`;

interface IEmployeeLatestPayoutProps {
  siteId?: string;
  dataCy?: string;
}

const EmployeeLatestPayout: React.FC<IEmployeeLatestPayoutProps> = ({ siteId, dataCy = 'employee-latest-payout' }) => {
  const dispatch = useDispatch();
  const payouts = useSelector((state: IStore) => state.user?.latestPayouts);
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, User.GET_USER_LATEST_PAYOUTS_REQUEST));
  const paymentId = payouts?.data?.[0]?.paymentId;

  useEffect(() => {
    dispatch(getUserLatestPayoutsRequest(siteId));
  }, [dispatch, siteId]);

  const calculatedTotal = useMemo(() => payouts?.data?.filter((payout) => payout.paymentId === paymentId).reduce((acc, payout) => acc + (payout?.net || 0), 0) || 0, [payouts, paymentId]);

  return (
    <Layout data-cy={dataCy}>
      {isLoading ? (
        <Skeleton width={100} />
      ) : (
        <Text size="xsmall" fontWeight="medium" data-cy={`${dataCy}-label`}>
          Latest Payment
        </Text>
      )}
      {isLoading ? (
        <Skeleton width={125} height={25} />
      ) : (
        <H2 fontWeight="bold" data-cy={`${dataCy}-amount`}>
          {payouts && paymentId ? centsToDollarsDisplay(calculatedTotal) : '--'}
        </H2>
      )}
      {isLoading ? (
        <Skeleton width={125} />
      ) : (
        <Text size="xxsmall" data-cy={`${dataCy}-date`}>
          {payouts?.data?.[0]?.createdAt ? formatDateAndTime(payouts?.data?.[0]?.createdAt) : '--'}
        </Text>
      )}
    </Layout>
  );
};

export default EmployeeLatestPayout;
